/**
 * Validator for NurseryNet backup files.
 * Checks structure, required fields, types, and referential integrity
 * before allowing a restore.
 */

export type ValidationSeverity = "error" | "warning" | "info";

export interface ValidationIssue {
  severity: ValidationSeverity;
  table?: string;
  field?: string;
  rowIndex?: number;
  message: string;
}

export interface ValidationReport {
  isValid: boolean; // true if no errors (warnings allowed)
  fileName?: string;
  meta?: {
    nursery_id?: string;
    nursery_name?: string;
    exported_at?: string;
    version?: number;
    tables?: string[];
    counts?: Record<string, number>;
  };
  totalTables: number;
  totalRows: number;
  errorCount: number;
  warningCount: number;
  issues: ValidationIssue[];
  tableSummary: Record<string, { rows: number; errors: number; warnings: number }>;
}

// Required tables that MUST be present (even if empty)
const REQUIRED_TABLES = ["nursery", "children", "classes", "staff"];

// Schema definition: required fields per table
const SCHEMA: Record<string, { required: string[]; types?: Record<string, "string" | "number" | "boolean" | "uuid" | "date" | "json"> }> = {
  nursery: {
    required: ["id", "name"],
    types: { id: "uuid", name: "string" },
  },
  children: {
    required: ["id", "full_name", "nursery_id"],
    types: { id: "uuid", full_name: "string", nursery_id: "uuid" },
  },
  classes: {
    required: ["id", "name", "nursery_id"],
    types: { id: "uuid", name: "string", nursery_id: "uuid" },
  },
  staff: {
    required: ["id", "full_name", "nursery_id"],
    types: { id: "uuid", full_name: "string", nursery_id: "uuid" },
  },
  attendance: {
    required: ["id", "nursery_id"],
    types: { id: "uuid", nursery_id: "uuid" },
  },
  lessons: {
    required: ["id", "title", "nursery_id"],
    types: { id: "uuid", title: "string", nursery_id: "uuid" },
  },
  invoices: {
    required: ["id", "nursery_id", "amount"],
    types: { id: "uuid", nursery_id: "uuid", amount: "number" },
  },
  expenses: {
    required: ["id", "nursery_id", "amount"],
    types: { id: "uuid", nursery_id: "uuid", amount: "number" },
  },
  payroll_records: {
    required: ["id", "nursery_id"],
    types: { id: "uuid", nursery_id: "uuid" },
  },
  exams: {
    required: ["id", "title", "nursery_id"],
    types: { id: "uuid", title: "string", nursery_id: "uuid" },
  },
  assignments: {
    required: ["id", "title", "nursery_id"],
    types: { id: "uuid", title: "string", nursery_id: "uuid" },
  },
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUUID(v: any): boolean {
  return typeof v === "string" && UUID_RE.test(v);
}

function isISODate(v: any): boolean {
  if (typeof v !== "string") return false;
  const d = new Date(v);
  return !isNaN(d.getTime());
}

function checkType(
  value: any,
  type: "string" | "number" | "boolean" | "uuid" | "date" | "json"
): boolean {
  if (value === null || value === undefined) return true; // null allowed; required is checked elsewhere
  switch (type) {
    case "string":
      return typeof value === "string";
    case "number":
      return typeof value === "number" && !isNaN(value);
    case "boolean":
      return typeof value === "boolean";
    case "uuid":
      return isUUID(value);
    case "date":
      return isISODate(value);
    case "json":
      return typeof value === "object";
  }
}

export function validateBackup(raw: unknown, fileName?: string): ValidationReport {
  const issues: ValidationIssue[] = [];
  const tableSummary: Record<string, { rows: number; errors: number; warnings: number }> = {};
  let totalRows = 0;

  const addIssue = (issue: ValidationIssue) => {
    issues.push(issue);
    if (issue.table) {
      const t = (tableSummary[issue.table] ||= { rows: 0, errors: 0, warnings: 0 });
      if (issue.severity === "error") t.errors++;
      else if (issue.severity === "warning") t.warnings++;
    }
  };

  // 1) Top-level structure
  if (!raw || typeof raw !== "object") {
    return {
      isValid: false,
      fileName,
      totalTables: 0,
      totalRows: 0,
      errorCount: 1,
      warningCount: 0,
      tableSummary: {},
      issues: [{ severity: "error", message: "الملف ليس JSON صحيح أو فارغ" }],
    };
  }

  const obj = raw as Record<string, any>;

  if (!obj.meta || typeof obj.meta !== "object") {
    addIssue({ severity: "error", message: "الملف لا يحتوي على قسم 'meta' (تلميذ النسخة)" });
  }
  if (!obj.data || typeof obj.data !== "object") {
    addIssue({
      severity: "error",
      message: "الملف لا يحتوي على قسم 'data' الذي يضم بيانات الجداول",
    });
  }

  const meta = obj.meta || {};
  const data: Record<string, any[]> = obj.data || {};

  // 2) Validate meta
  if (meta.version && typeof meta.version === "number" && meta.version > 1) {
    addIssue({
      severity: "warning",
      message: `إصدار النسخة (${meta.version}) أحدث من المتوقع (1) — قد تحدث مشاكل`,
    });
  }
  if (meta.nursery_id && !isUUID(meta.nursery_id)) {
    addIssue({ severity: "error", message: "حقل meta.nursery_id ليس UUID صالح" });
  }
  if (meta.exported_at && !isISODate(meta.exported_at)) {
    addIssue({
      severity: "warning",
      message: "حقل meta.exported_at ليس تاريخًا صالحًا",
    });
  }

  // 3) Required tables
  for (const t of REQUIRED_TABLES) {
    if (!(t in data)) {
      addIssue({
        severity: "error",
        table: t,
        message: `جدول مطلوب مفقود: ${t}`,
      });
    }
  }

  // 4) Validate each table
  const expectedNurseryId = isUUID(meta.nursery_id) ? meta.nursery_id : null;
  const childrenIds = new Set<string>();
  const classIds = new Set<string>();
  const staffIds = new Set<string>();

  // First pass — collect IDs for FK checks
  for (const [table, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) continue;
    if (table === "children") rows.forEach((r) => r?.id && childrenIds.add(r.id));
    if (table === "classes") rows.forEach((r) => r?.id && classIds.add(r.id));
    if (table === "staff") rows.forEach((r) => r?.id && staffIds.add(r.id));
  }

  // Second pass — full validation
  for (const [table, rows] of Object.entries(data)) {
    if (!Array.isArray(rows)) {
      addIssue({
        severity: "error",
        table,
        message: `الجدول '${table}' ليس مصفوفة (array)`,
      });
      continue;
    }

    tableSummary[table] ||= { rows: rows.length, errors: 0, warnings: 0 };
    tableSummary[table].rows = rows.length;
    totalRows += rows.length;

    const schema = SCHEMA[table];
    const seenIds = new Set<string>();

    rows.forEach((row, idx) => {
      if (!row || typeof row !== "object") {
        addIssue({
          severity: "error",
          table,
          rowIndex: idx,
          message: `السجل #${idx} ليس كائنًا (object)`,
        });
        return;
      }

      // Required fields
      if (schema) {
        for (const field of schema.required) {
          if (row[field] === undefined || row[field] === null || row[field] === "") {
            addIssue({
              severity: "error",
              table,
              rowIndex: idx,
              field,
              message: `حقل مطلوب مفقود: ${field}`,
            });
          }
        }
        // Types
        if (schema.types) {
          for (const [field, type] of Object.entries(schema.types)) {
            if (row[field] !== undefined && row[field] !== null) {
              if (!checkType(row[field], type)) {
                addIssue({
                  severity: "error",
                  table,
                  rowIndex: idx,
                  field,
                  message: `نوع غير صحيح للحقل '${field}': المتوقع ${type}`,
                });
              }
            }
          }
        }
      }

      // Duplicate IDs
      if (row.id) {
        if (seenIds.has(row.id)) {
          addIssue({
            severity: "error",
            table,
            rowIndex: idx,
            field: "id",
            message: `معرّف مكرر: ${row.id}`,
          });
        } else {
          seenIds.add(row.id);
        }
      }

      // Tenant isolation: nursery_id must match meta
      if (expectedNurseryId && row.nursery_id && row.nursery_id !== expectedNurseryId && table !== "nursery") {
        addIssue({
          severity: "error",
          table,
          rowIndex: idx,
          field: "nursery_id",
          message: `nursery_id لا يطابق الحضانة في meta (تخصّ حضانة أخرى)`,
        });
      }

      // FK checks
      if (table === "attendance" || table === "exam_submissions" || table === "assignment_submissions" || table === "child_observations" || table === "invoices") {
        if (row.child_id && !childrenIds.has(row.child_id)) {
          addIssue({
            severity: "warning",
            table,
            rowIndex: idx,
            field: "child_id",
            message: `child_id يشير إلى طفل غير موجود في النسخة: ${row.child_id}`,
          });
        }
      }
      if (table === "children" && row.class_id && !classIds.has(row.class_id)) {
        addIssue({
          severity: "warning",
          table,
          rowIndex: idx,
          field: "class_id",
          message: `class_id يشير إلى فصل غير موجود في النسخة: ${row.class_id}`,
        });
      }
      if (table === "classes" && row.teacher_id && !staffIds.has(row.teacher_id)) {
        addIssue({
          severity: "warning",
          table,
          rowIndex: idx,
          field: "teacher_id",
          message: `teacher_id يشير إلى موظف غير موجود في النسخة: ${row.teacher_id}`,
        });
      }
    });
  }

  // 5) Count consistency vs meta
  if (meta.counts && typeof meta.counts === "object") {
    for (const [t, declared] of Object.entries(meta.counts as Record<string, number>)) {
      const actual = Array.isArray(data[t]) ? data[t].length : 0;
      if (actual !== declared) {
        addIssue({
          severity: "warning",
          table: t,
          message: `عدد السجلات الفعلي (${actual}) لا يطابق المعلَن في meta (${declared})`,
        });
      }
    }
  }

  const errorCount = issues.filter((i) => i.severity === "error").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;

  return {
    isValid: errorCount === 0,
    fileName,
    meta,
    totalTables: Object.keys(data).length,
    totalRows,
    errorCount,
    warningCount,
    issues,
    tableSummary,
  };
}
